import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Save, Key, Bell, Shield, Globe } from 'lucide-react';
import { toast } from 'sonner';

export default function SuperAdminSettings() {
  const handleSave = () => {
    toast.success('Settings saved successfully');
  };

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl text-gray-900">Settings</h1>
        <p className="text-gray-600 mt-1">Configure system settings and preferences</p>
      </div>

      {/* Settings Tabs */}
      <Tabs defaultValue="general" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="general">
            <Globe className="w-4 h-4 mr-2" />
            General
          </TabsTrigger>
          <TabsTrigger value="api">
            <Key className="w-4 h-4 mr-2" />
            API Keys
          </TabsTrigger>
          <TabsTrigger value="notifications">
            <Bell className="w-4 h-4 mr-2" />
            Notifications
          </TabsTrigger>
          <TabsTrigger value="security">
            <Shield className="w-4 h-4 mr-2" />
            Security
          </TabsTrigger>
        </TabsList>

        {/* General Settings */}
        <TabsContent value="general" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Platform Configuration</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Platform Name</Label>
                  <Input defaultValue="AIS Connect - UAE E-Invoicing Portal" />
                </div>
                <div className="space-y-2">
                  <Label>Support Email</Label>
                  <Input defaultValue="support@ais-erp.com" />
                </div>
              </div>

              <div className="space-y-2">
                <Label>FTA Peppol Endpoint</Label>
                <Input defaultValue="https://api.fta.gov.ae/peppol/v1" />
              </div>

              <div className="space-y-2">
                <Label>Default Service Provider URL</Label>
                <Input defaultValue="https://api.serviceprovider.com/v2" />
              </div>

              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <p className="text-sm text-gray-900">Enable Auto-Retry Failed Transactions</p>
                  <p className="text-xs text-gray-600">Automatically retry failed API calls up to 3 times</p>
                </div>
                <Switch defaultChecked />
              </div>

              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <p className="text-sm text-gray-900">Maintenance Mode</p>
                  <p className="text-xs text-gray-600">Block all API requests during maintenance</p>
                </div>
                <Switch />
              </div>

              <Button onClick={handleSave} className="bg-[#0052CC] hover:bg-[#0041A3]">
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* API Keys */}
        <TabsContent value="api" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>API Authentication Keys</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>FTA Peppol API Key</Label>
                <div className="flex space-x-2">
                  <Input type="password" defaultValue="fta_key_1234567890abcdef" className="flex-1" />
                  <Button variant="outline">Regenerate</Button>
                </div>
                <p className="text-xs text-gray-600">Last regenerated: 2025-08-15</p>
              </div>

              <div className="space-y-2">
                <Label>Service Provider API Key</Label>
                <div className="flex space-x-2">
                  <Input type="password" defaultValue="sp_key_abcdef1234567890" className="flex-1" />
                  <Button variant="outline">Regenerate</Button>
                </div>
                <p className="text-xs text-gray-600">Last regenerated: 2025-09-20</p>
              </div>

              <div className="space-y-2">
                <Label>Webhook Secret</Label>
                <div className="flex space-x-2">
                  <Input type="password" defaultValue="whsec_9876543210fedcba" className="flex-1" />
                  <Button variant="outline">Regenerate</Button>
                </div>
                <p className="text-xs text-gray-600">Used for validating incoming webhooks</p>
              </div>

              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  <strong>Warning:</strong> Regenerating API keys will invalidate all existing keys. 
                  Make sure to update all client configurations.
                </p>
              </div>

              <Button onClick={handleSave} className="bg-[#0052CC] hover:bg-[#0041A3]">
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notifications */}
        <TabsContent value="notifications" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Email Notifications</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <p className="text-sm text-gray-900">New Client Registration</p>
                  <p className="text-xs text-gray-600">Notify when a new client signs up</p>
                </div>
                <Switch defaultChecked />
              </div>

              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <p className="text-sm text-gray-900">Subscription Expiring</p>
                  <p className="text-xs text-gray-600">Alert 7 days before subscription expires</p>
                </div>
                <Switch defaultChecked />
              </div>

              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <p className="text-sm text-gray-900">API Rate Limit Exceeded</p>
                  <p className="text-xs text-gray-600">Notify when client exceeds API rate limits</p>
                </div>
                <Switch defaultChecked />
              </div>

              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <p className="text-sm text-gray-900">System Errors</p>
                  <p className="text-xs text-gray-600">Alert on critical system errors</p>
                </div>
                <Switch defaultChecked />
              </div>

              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <p className="text-sm text-gray-900">Daily Summary Report</p>
                  <p className="text-xs text-gray-600">Receive daily summary at 9:00 AM</p>
                </div>
                <Switch />
              </div>

              <Button onClick={handleSave} className="bg-[#0052CC] hover:bg-[#0041A3]">
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Security */}
        <TabsContent value="security" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Security Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Minimum Password Length</Label>
                <Input type="number" defaultValue="12" />
              </div>

              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <p className="text-sm text-gray-900">Require Two-Factor Authentication</p>
                  <p className="text-xs text-gray-600">Force all super admins to use 2FA</p>
                </div>
                <Switch defaultChecked />
              </div>

              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <p className="text-sm text-gray-900">IP Whitelisting</p>
                  <p className="text-xs text-gray-600">Restrict admin access to specific IP addresses</p>
                </div>
                <Switch />
              </div>

              <div className="space-y-2">
                <Label>Allowed IP Addresses (comma-separated)</Label>
                <Textarea 
                  placeholder="192.168.1.1, 10.0.0.1"
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label>Session Timeout (minutes)</Label>
                <Input type="number" defaultValue="60" />
              </div>

              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <p className="text-sm text-gray-900">Enable Audit Logging</p>
                  <p className="text-xs text-gray-600">Log all admin actions for compliance</p>
                </div>
                <Switch defaultChecked />
              </div>

              <Button onClick={handleSave} className="bg-[#0052CC] hover:bg-[#0041A3]">
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
